import { useLazyQuery, useMutation } from '@apollo/client';
import { Box, Button } from '@material-ui/core';
import { TextNormal, TitlePage } from 'component/customize_style_component/styled_title';
import CustomizeLayoutQuiz from 'component/layout_quiz/layout_quiz';
import { useAuth } from 'contexts/AuthContext';
import { CREATE_ANSWER } from 'graph_ql/getQuiz/mutation';
import { GET_QUESTION_DATA, GET_QUIZ_RESPONSE } from 'graph_ql/getQuiz/queries';
import { GET_USER_BY_USERNAME } from 'graph_ql/users/queries';
import { useSpinnerLoading } from 'hooks/useSpinnerLoading';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import themes from 'styles/themes';
import SwiperCore from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useGetGender } from '../../../hooks/useGetGender';
import { PATH } from '../../../routes/constant';
import Picker from 'component/ios_picker/iosPicker';
import { useStyles } from './styles';
import { Spinner } from 'react-bootstrap';
import { EQuizOrder, getQuizData } from '../setup_quiz';
import { EGENDER, quizContextType } from 'types/quiz';
import { HelpModal } from 'component/layout_quiz/help-modal';
import { trackEvent, TRACK_EVENT } from 'utils/analytics';
import { QuizSpinner } from 'component/layout_quiz/quiz-spinner';
import { getMeasurementQuizId } from 'utils/utils';
import { STRINGS } from '../../../constants/strings';

const convertQuestionDataRangeToListOptions = (questionData: any) => {
    const rangeArr = questionData.value.split(',');
    const startValue = parseInt(rangeArr[0]);
    const endValue = parseInt(rangeArr[1]);
    const returnedList = [] as any[];
    for (let i = startValue; i <= endValue; i++) {
        returnedList.push({
            title: `${i} ${questionData.descriptions[0]}`,
            value: `${i} ${questionData.descriptions[0]}`,
            id: questionData.id,
        });
    }
    return returnedList;
};

const mapQuestionDataToQuestions = (listQuestions, listQuestionDatas, questionIndex) => {
    const updatedListQuestions = listQuestions.map((item, index) => {
        if (index === questionIndex) {
            item.listOptions = convertQuestionDataRangeToListOptions(listQuestionDatas[0]);
            return { ...item };
        } else return { ...item };
    });
    return updatedListQuestions;
};

const mapQuenstionInfoToQuestions = (listQuestions, listQuestionInfo) => {
    return listQuestions.map((item, index) => {
        const mappedQuestionInfo = listQuestionInfo.find((info) => info.id === item.id);
        if (mappedQuestionInfo) {
            return Object.assign({
                ...item,
                questionInfo: mappedQuestionInfo,
            });
        } else return { ...item };
    });
};

const mapAnswersToQuestions = (arrQuestions, arrAnswers) => {
    return arrQuestions.map((question, i) => {
        const mappedAnswer = arrAnswers.find((answer) => question.id === answer.questionId);
        if (mappedAnswer) {
            return Object.assign({
                ...question,
                answerId: mappedAnswer?.id,
                answerUpdated: mappedAnswer?.updated,
                valueAnswer: mappedAnswer?.valueAnswer,
                questionDataIds: mappedAnswer?.questionDataIds,
            });
        } else {
            return { ...question };
        }
    });
};

export interface IQuizProps {
    handleNext?: (value: Boolean, order?: number, gender?: EGENDER) => void;
}

interface IQuestionSlideItem {
    id: string;
    listOptions: Array<{
        title: string;
        isSelect: boolean;
    }>;
    questionDataSubject: string;
    questionDataIds: Array<string>;
    questionDataSubjectTags: Array<string>;
    valueAnswer: string;
    subTitle: string;
    title: string;
    type: string;
    answerUpdated?: boolean;
    answerId?: string;
}

export default function MeasurementQuiz(props: IQuizProps) {
    const { userName } = useParams<{ userName: string }>();
    const { currentUser } = useAuth();
    const { handleNext } = props;
    const history = useHistory();
    const gender = useGetGender(Boolean(userName));
    const location = useLocation();
    const classes = useStyles();
    const [quizData, setQuizData] = useState<any>(null);
    const [swiperInstance, setSwiperInstance] = useState<SwiperCore>();
    const [question, setQuestion] = useState<any>(null);
    const [listQuestionSize, setListQuestionSize] = useState<Array<IQuestionSlideItem>>([]);
    const [currentSlideRealIndex, setCurrentSlideRealIndex] = useState(0);
    const [listAnswer, setListAnswer] = useState<any[]>([]);
    const [savedAnswers, setSavedAnswers] = useState<any[]>([]);
    const isConsumerView = () => Boolean(!userName);
    const [helpModalVisible, setHelpModalVisible] = useState<Boolean>(false);
    const [pendingSaveApi, setPendingSaveApi] = useState(0);
    const [pendingNextApiLoading, setPendingNextApiLoading] = useState(false);
    const [pendingSkipApiLoading, setPendingSkipApiLoading] = useState(false);
    const shouldAllowSlideNext = useMemo(() => {
        if (location.pathname !== PATH.QUIZ_MANAGER) {
            return true;
        }

        if (listQuestionSize.length > 0 && listAnswer.length > 0 && swiperInstance) {
            const mappedAnswer = listAnswer.find(
                (answer) => listQuestionSize[currentSlideRealIndex].id === answer.questionId,
            );
            if (mappedAnswer && mappedAnswer.submitted) {
                swiperInstance.allowSlideNext = true;
                return true;
            } else {
                swiperInstance.allowSlideNext = false;
                return false;
            }
        } else {
            return false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listQuestionSize, currentSlideRealIndex]);

    //Initial Query
    const [getUserByUserName, { loading: userLoading }] = useLazyQuery(GET_USER_BY_USERNAME, {
        onCompleted(data) {
            if (listQuestionSize.length <= 0) {
                getQuizResponse({
                    variables: {
                        input: {
                            quizId: getQuizData(EQuizOrder.MEASUREMENT, gender)?.id,
                            contextType: quizContextType,
                            contextId: getQuizData(EQuizOrder.MEASUREMENT, gender)?.id,
                            userId: data.getUserByUserName?.id,
                        },
                    },
                });
            }
        },
    });
    const [getQuestionData, { loading: questionDataLoading }] = useLazyQuery(GET_QUESTION_DATA, {
        onCompleted(data) {
            const listQuestionDatas = data.getQuestionData.data;
            setListQuestionSize(mapQuestionDataToQuestions(listQuestionSize, listQuestionDatas, currentSlideRealIndex));
        },
    });
    const createAnswer = async (input: {
        questionId: String;
        questionSubject: String;
        contextType: String;
        contextId: String;
        value: any;
        valueType: String;
        questionDataIds: string[];
        id?: string;
    }) => {
        const variables = {
            input,
        };
        await answerMutation({ variables });
    };
    const [answerMutation] = useMutation(CREATE_ANSWER, {
        onCompleted(data) {
            setPendingSaveApi((prevValue) => prevValue - 1);
            setSavedAnswers([...savedAnswers, { ...data?.createAnswer }]);
        },
        onError(error) {
            console.error('Create Failed: ', error);
        },
    });
    const [getQuizResponse, { loading: loadingQuiz }] = useLazyQuery(GET_QUIZ_RESPONSE, {
        fetchPolicy: 'no-cache',
        onCompleted(data) {
            setQuestion(_.valuesIn(data.getQuizResponse?.questions)[0]);
            setQuizData(data.getQuizResponse);
            const answers = _.valuesIn(data.getQuizResponse?.answers);
            const listAnswers = answers.map((answer) => {
                return {
                    id: answer.id,
                    questionId: answer.questionId,
                    valueAnswer: answer.value,
                    questionDataSubject: answer?.questionDatas[0]?.subject,
                    submitted: true,
                };
            });
            setListAnswer(listAnswers);
            const listQuestions = _.valuesIn(data.getQuizResponse?.questions);
            const arrSizeOptions: any[] = listQuestions.map((item) => {
                return {
                    id: item.id,
                    questionId: item.id,
                    listOptions: [],
                    questionDataSubject: item.questionDataSubject,
                    questionDataSubjectTags: item.questionDataSubjectTags,
                    subTitle: item.subTitle,
                    title: item.title,
                    type: item.type,
                };
            });
            const mappedQuestionsAnswers = mapAnswersToQuestions(arrSizeOptions, listAnswers);
            setListQuestionSize(mappedQuestionsAnswers);
            setListQuestionSize(
                mapQuenstionInfoToQuestions(mappedQuestionsAnswers, data.getQuizResponse?.questionsInfo),
            );
            getQuestionData({
                variables: {
                    input: {
                        subject: listQuestions[0].questionDataSubject,
                        tags: listQuestions[0].questionDataSubjectTags,
                        paging: { page: 0, size: 0 },
                        enabled: true,
                    },
                },
            });
        },
    });

    useEffect(() => {
        if (userName) {
            getUserByUserName({
                variables: {
                    input: {
                        userName,
                    },
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userName]);
    useEffect(() => {
        if (currentUser && isConsumerView() && gender && listQuestionSize.length <= 0) {
            getQuizResponse({
                variables: {
                    input: {
                        quizId: getQuizData(EQuizOrder.MEASUREMENT, gender)?.id,
                        contextType: quizContextType,
                        contextId: getQuizData(EQuizOrder.MEASUREMENT, gender)?.id,
                        userId: currentUser.uid,
                    },
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gender]);

    // Function
    const handleChangeSlide = (e) => {
        setCurrentSlideRealIndex(e.realIndex);
        if (listQuestionSize[e.realIndex]) {
            getQuestionData({
                variables: {
                    input: {
                        subject: listQuestionSize[e.realIndex]?.questionDataSubject,
                        tags: listQuestionSize[e.realIndex]?.questionDataSubjectTags,
                        paging: { page: 0, size: 0 },
                        enabled: true,
                    },
                },
            });
        }
    };
    const handleSelectSize = async (slide, itemSelect) => {
        const input: any = {
            questionId: slide.questionId,
            questionSubject: slide.questionDataSubject,
            contextType: quizContextType,
            contextId: getQuizData(EQuizOrder.MEASUREMENT, gender)?.id,
            value: itemSelect.title,
            valueType: 'STRING',
            questionDataIds: [itemSelect.id],
        };
        if (slide.answerId) {
            input.id = slide.answerId;
        }
        updateListAnswerData(input);
    };

    const updateListAnswerData = (updatedAnswer) => {
        const updatedAnswerIndex = listAnswer.findIndex((answer) => answer.questionId === updatedAnswer.questionId);
        // Update
        if (updatedAnswerIndex >= 0) {
            listAnswer[updatedAnswerIndex] = {
                ...updatedAnswer,
                submitted: listAnswer[updatedAnswerIndex].submitted,
                valueAnswer: updatedAnswer.value,
                updated: true,
            };
        }
        // Add new
        else {
            listAnswer.push({ ...updatedAnswer, valueAnswer: updatedAnswer.value, updated: true });
        }
        setListAnswer(listAnswer);
        setListQuestionSize(mapAnswersToQuestions(listQuestionSize, listAnswer));
    };

    const handleSaveLastAnswer = useCallback(() => {
        if (location.pathname === PATH.QUIZ_MANAGER) {
            // Get quiz manager up-to-date
            if (handleNext) {
                handleNext(false, EQuizOrder.MEASUREMENT, gender === EGENDER.MALE ? EGENDER.MALE : EGENDER.FEMALE);
            }
            return;
        } else {
            setPendingNextApiLoading(false);
            setPendingSkipApiLoading(false);
        }
    }, [gender, handleNext, location.pathname]);

    useEffect(() => {
        if ((pendingNextApiLoading || pendingSkipApiLoading) && !pendingSaveApi) {
            handleSaveLastAnswer();
        }
    }, [pendingNextApiLoading, pendingSkipApiLoading, pendingSaveApi, handleSaveLastAnswer]);

    const updateSubmittedAnswer = (updatedAnswer) => {
        setListAnswer(
            listAnswer.map((answer) => {
                if (answer.questionId === updatedAnswer.questionId) {
                    return { ...answer, submitted: true };
                } else {
                    return answer;
                }
            }),
        );
    };

    const submitAnswer = async (answer) => {
        if (isConsumerView() && answer) {
            await createAnswer(answer);
        }
    };

    const processNextQuiz = async (isSkipped?: boolean) => {
        if (swiperInstance) {
            try {
                const currentQuestion = listQuestionSize[currentSlideRealIndex];
                let answerId = currentQuestion?.answerId;
                if (!answerId) {
                    answerId = savedAnswers.find((item) => item?.questionId === currentQuestion.id)?.id;
                }

                const answer = {
                    questionId: currentQuestion.id,
                    questionSubject: currentQuestion.questionDataSubject,
                    contextType: quizContextType,
                    contextId: getQuizData(EQuizOrder.MEASUREMENT, gender)?.id,
                    value: !isSkipped ? currentQuestion.valueAnswer : '',
                    valueType: 'STRING',
                    questionDataIds: currentQuestion.questionDataIds,
                    id: answerId,
                    isSkipped,
                };
                setPendingSaveApi((prevValue) => prevValue + 1);
                submitAnswer(answer);
                updateSubmittedAnswer(answer);
            } catch (error) {
                console.debug(error);
            }
        }

        // NOT LAST QUESTION
        if (currentSlideRealIndex < listQuestionSize.length - 1 && swiperInstance) {
            swiperInstance.allowSlideNext = true;
            swiperInstance.slideNext();
            swiperInstance.allowSlideNext = shouldAllowSlideNext;
        }
        // LAST QUESTION
        else {
            if (location.pathname === PATH.MEASUREMENTS_QUIZ) {
                history.push(PATH.PROFILE);
                return;
            }
            if (isSkipped) {
                setPendingSkipApiLoading(true);
            } else {
                setPendingNextApiLoading(true);
            }
        }
    };

    useEffect(() => {
        if (
            listQuestionSize?.length &&
            location.pathname === PATH.MEASUREMENTS_QUIZ &&
            currentSlideRealIndex === listQuestionSize.length - 1
        ) {
            // setShowDoneButton(true);
        } else {
            // setShowDoneButton(false);
        }
    }, [currentSlideRealIndex, listQuestionSize, location, location.pathname]);

    // Handle Loading Spinner
    useSpinnerLoading('consumer', userLoading);

    const SubmitButton = (props: { index; active }) => {
        return (
            <button
                type="submit"
                key={props.index}
                className={classes.button}
                style={{
                    color: themes.palette.common.white,
                    backgroundColor: props.active ? themes.palette.primary.main : '#A3A3A3',
                }}
                onClick={async () => {
                    await processNextQuiz(false);
                }}
                disabled={!props.active}
            >
                {location.pathname !== PATH.QUIZ_MANAGER ? STRINGS.UPDATE : STRINGS.SAVE}{' '}
                {pendingNextApiLoading && (
                    <Spinner animation="border" style={{ marginLeft: 10, width: 18, height: 18 }} />
                )}
            </button>
        );
    };

    const renderSkipBtn = (question) => {
        if (!question?.questionInfo?.required && location.pathname === PATH.QUIZ_MANAGER) {
            return (
                <Button
                    type="submit"
                    className={classes.skipBtn}
                    onClick={async () => {
                        await processNextQuiz(true);
                    }}
                >
                    {STRINGS.RESPOND_LATER}
                    {pendingSkipApiLoading && (
                        <Spinner animation="border" style={{ marginLeft: 10, width: 18, height: 18 }} />
                    )}
                </Button>
            );
        }
        return null;
    };

    const renderSingleSelectItem = (slide, index) => {
        if (slide.listOptions.length > 0) {
            return (
                <Box className={classes.styleContainer}>
                    <Picker
                        optionGroups={{
                            name: slide.listOptions.map((option) => {
                                return {
                                    label: option.title,
                                    value: option.value,
                                    id: option.id,
                                };
                            }),
                        }}
                        valueGroups={{
                            name: slide.valueAnswer,
                        }}
                        onChange={(name, value, selectedOption) => {
                            if (isConsumerView() && value)
                                handleSelectSize(slide, { title: value, id: selectedOption.id });
                            else return;
                        }}
                    />
                </Box>
            );
        }

        if (questionDataLoading) {
            return (
                <div className={`${classes.flexCenter} ${classes.pickerContainer} picker-container`}>
                    <Spinner animation="border" style={{ marginLeft: 10, width: 18, height: 18 }} />
                </div>
            );
        }

        return <div className={`${classes.flexCenter} ${classes.pickerContainer} picker-container`}></div>;
    };

    const handleClickQuizHelp = () => {
        setHelpModalVisible(true);
        trackEvent(TRACK_EVENT.USER_QUIZ_HELP_OPEN, {
            email: currentUser.email,
            userId: currentUser.uid,
            quizId: quizData.id,
        });
    };

    return (
        <CustomizeLayoutQuiz
            quizId={getMeasurementQuizId(gender)}
            title={Boolean(!userName) ? quizData?.subTitle : ''}
            titlePage={quizData?.title}
            handleAnswer={processNextQuiz}
            isUpdate={location.pathname === PATH.MEASUREMENTS_QUIZ}
            isDisplayContent={isConsumerView()}
            hideBottomNav={location.pathname === PATH.QUIZ_MANAGER}
            isHiddenAction={true}
            loading={false}
            requiredQuestion={true}
            handleSkipQuestion={() => {}}
        >
            {loadingQuiz && <QuizSpinner />}
            <Box className={classes.helpTitle} onClick={() => handleClickQuizHelp()}>
                {quizData?.help?.title}
            </Box>
            <Box
                textAlign={'center'}
                display="flex"
                flexDirection={'column'}
                width={'100%'}
                height={'100%'}
                id="swiper-wrapper"
                position={'relative'}
            >
                {isConsumerView() && (
                    <Box id="swiper-title" marginTop="14px">
                        <TextNormal color={themes.palette.grey['300']}>{question?.subTitle}</TextNormal>
                    </Box>
                )}
                <Box display={'flex'} flexDirection={'column'} textAlign={'center'} width={'100%'} height={`100%`}>
                    <Swiper
                        direction="horizontal"
                        onSlideChange={handleChangeSlide}
                        onSwiper={setSwiperInstance}
                        slidesPerView={1.2}
                        centeredSlides={true}
                        spaceBetween={10}
                        className={classes.fitContent}
                        allowSlideNext={shouldAllowSlideNext}
                        mousewheel
                    >
                        {listQuestionSize?.map((slide, index) => {
                            return (
                                <SwiperSlide key={index}>
                                    <Box className={classes.sizesWrapper}>
                                        <Box padding={'0 0 12px 0'} marginTop={'auto'}>
                                            <TitlePage size={24}>{(slide.title as string).toUpperCase()}</TitlePage>
                                        </Box>
                                        <Box
                                            position={'relative'}
                                            maxHeight="316px"
                                            width={'100%'}
                                            bgcolor={'white'}
                                            borderRadius={'20px'}
                                            display="flex"
                                            flexDirection={'column'}
                                            marginBottom={'auto'}
                                        >
                                            {renderSingleSelectItem(slide, index)}
                                        </Box>
                                        {currentSlideRealIndex === index ? (
                                            <>
                                                <SubmitButton
                                                    index={index}
                                                    active={
                                                        listQuestionSize[currentSlideRealIndex].answerUpdated &&
                                                        !pendingNextApiLoading &&
                                                        !pendingSkipApiLoading
                                                    }
                                                />
                                            </>
                                        ) : (
                                            <div style={{ height: 44, marginBottom: 12 }} />
                                        )}
                                        {renderSkipBtn(slide)}
                                    </Box>
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </Box>
            </Box>
            <HelpModal
                data={quizData?.help}
                visible={helpModalVisible}
                onHide={() => {
                    setHelpModalVisible(false);
                }}
            />
        </CustomizeLayoutQuiz>
    );
}
